import { useLocation, useNavigate } from "react-router-dom";
import {
  Activity,
  Lightbulb,
  LogOut,
  MapPin,
  User,
  LayoutDashboard,
} from "lucide-react";

import { useLogout } from "@/hooks/useAuth";

import { DropdownMenu, type DropdownMenuItemSpec } from "./DropdownMenu";

/**
 * Menu déroulant du compte connecté (S13-4, refondu S16-2).
 *
 * Affiche l'email du compte authentifié + un menu déroulant accessible clavier.
 * S16-2 (« tout dans l'avatar », décision PO 2026-06-12) : la barre du haut est
 * minimale (logo + « Nouvelle simulation »), **toutes** les autres destinations
 * vivent dans ce menu. Vocabulaire figé (une destination = un seul libellé) :
 * - **Tableau de bord** → `/app` (ex-« Mes scénarios », libellé supprimé).
 * - **Mes sites** → `/sites`.
 * - **Profil** → `/settings`.
 * - **Déconnexion** → redirection `/` **puis** `useLogout()` (l'ordre importe :
 *   on quitte la vue scopée-compte avant que le logout n'invalide le cache,
 *   sinon les queries encore montées prennent un 404 cross-owner).
 *
 * « Déconnecter tous les appareils » n'est **pas** dupliqué ici : il reste sur
 * la page `/settings` (livré S15-2).
 *
 * Toute l'a11y clavier (ouverture/fermeture, flèches, Échap, focus, clic
 * extérieur) est portée par `DropdownMenu`.
 */
export interface AccountMenuProps {
  email: string;
  /**
   * S21 (ADR-007 D4) — ajoute l'entrée « Surveillance plateforme » → `/admin`.
   * Masquage **cosmétique** : l'API garde réellement la page (403 sinon).
   */
  isAdmin?: boolean;
}

export function AccountMenu({ email, isAdmin = false }: AccountMenuProps): JSX.Element {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const logoutMutation = useLogout();

  // Page courante = item « sélectionné » (grisé) dans le menu. Match exact ou
  // sous-route (`/sites/:id`). Cf. `DropdownMenuItemSpec.current`.
  const isActive = (path: string): boolean =>
    pathname === path || pathname.startsWith(`${path}/`);

  const items: DropdownMenuItemSpec[] = [
    {
      label: "Tableau de bord",
      icon: <LayoutDashboard className="h-4 w-4" aria-hidden />,
      onSelect: () => navigate("/app"),
      current: isActive("/app"),
    },
    {
      label: "Mes sites",
      icon: <MapPin className="h-4 w-4" aria-hidden />,
      onSelect: () => navigate("/sites"),
      current: isActive("/sites"),
    },
    {
      label: "Profil",
      icon: <User className="h-4 w-4" aria-hidden />,
      onSelect: () => navigate("/settings"),
      current: isActive("/settings"),
    },
    // S21 — entrée admin (masquage cosmétique ; l'API garde réellement la page).
    ...(isAdmin
      ? [
          {
            label: "Surveillance plateforme",
            icon: <Activity className="h-4 w-4" aria-hidden />,
            onSelect: () => navigate("/admin"),
            current: isActive("/admin"),
          },
        ]
      : []),
    // S34-5 (épopée E) — entrée discrète vers la boîte à idées.
    {
      label: "Suggérer une idée",
      icon: <Lightbulb className="h-4 w-4" aria-hidden />,
      onSelect: () => navigate("/suggerer"),
      current: isActive("/suggerer"),
    },
    {
      label: "Déconnexion",
      icon: <LogOut className="h-4 w-4" aria-hidden />,
      onSelect: () => {
        // Naviguer AVANT de lancer le logout. `useLogout` invalide tout le
        // cache dans son `onSuccess`, et react-query exécute le `onSuccess` du
        // hook AVANT celui du `mutate`. Si on navigue dans le `onSuccess` du
        // `mutate`, l'invalidation se produit alors qu'une vue scopée au compte
        // (résultats de simu `/simulations/:id`, `/settings`…) est encore
        // montée : ses queries se re-tirent sous l'identité redevenue anonyme
        // et prennent un 404 (isolation cross-owner). Naviguer d'abord démonte
        // ces vues, donc l'invalidation ne re-tire plus aucune query interdite.
        navigate("/");
        logoutMutation.mutate();
      },
    },
  ];

  return (
    <DropdownMenu
      id="account-menu"
      // 2026-06-13 (décision PO) : on n'affiche QUE l'icône (un email long
      // déséquilibrait la barre). L'email reste exposé via l'`aria-label`
      // pour l'accessibilité et l'identification du compte.
      triggerAriaLabel={`Compte : ${email}`}
      triggerContent={<User className="h-4 w-4" aria-hidden />}
      items={items}
    />
  );
}
